import { MANUAL_LIFECYCLE_TRANSITIONS } from '@eluve/api-contract';
import { TagProps } from '@eluve/components';
import {
  DbEnums,
  TenantUserLifecycleStatusTypesEnum,
} from '@eluve/graphql-types';

export type LifecycleStatus = TenantUserLifecycleStatusTypesEnum;

type LifecycleTag = { color: TagProps['color']; label: string };

const { CREATED, ONBOARDED, ACTIVE, CHURNED } =
  DbEnums.TenantUserLifecycleStatus;

/**
 * Lifecycle status -> color-coded Tag, per the ENG-4694 design (Images #1/#2).
 * Single source of truth for both the Status cell and the "Set status" menu
 * labels so a label change can't drift between the two surfaces.
 */
const STATUS_TAG: Record<LifecycleStatus, LifecycleTag> = {
  [CREATED]: { color: 'lightblue', label: 'Created' },
  [ONBOARDED]: { color: 'yellow', label: 'Onboarded' },
  [ACTIVE]: { color: 'lightgreen', label: 'Active' },
  [CHURNED]: { color: 'negative', label: 'Churned' },
};

/**
 * Tag shown for excluded users (ENG-4688 / ENG-4685). Exclusion takes
 * precedence over the lifecycle status in the Status column (per design
 * Image #3: an excluded user shows "Excluded" instead of its lifecycle status),
 * so `LifecycleStatusTag` prefers this tag when `isExcluded` is set.
 */
export const EXCLUDED_TAG: LifecycleTag = { color: 'gray', label: 'Excluded' };

/**
 * Tag spec for a lifecycle status, or `null` when there is no status — invite
 * rows have no `tenant_users` row and render blank in the Status column.
 */
export const getLifecycleStatusTagProps = (
  status: LifecycleStatus | null | undefined,
): LifecycleTag | null => {
  if (!status) {
    return null;
  }
  return STATUS_TAG[status] ?? null;
};

/**
 * Human label for a lifecycle status (used by the "Set status" menu items).
 */
export const getLifecycleStatusLabel = (status: LifecycleStatus): string =>
  STATUS_TAG[status].label;

/**
 * Drives the "Set status" menu affordances from the authoritative shared table
 * (`MANUAL_LIFECYCLE_TRANSITIONS`) so the UI can't offer a move the API rejects.
 * ACTIVE is sweep-only, so it is never offered here.
 */
export const getManualLifecycleTransitions = (
  status: LifecycleStatus,
): LifecycleStatus[] => MANUAL_LIFECYCLE_TRANSITIONS[status] ?? [];

/**
 * Whether a transition needs a confirmation dialog. Both directions of Churned
 * prompt: moving a user *into* Churned and *out of* Churned (un-churn).
 */
export const requiresChurnConfirmation = (
  from: LifecycleStatus,
  to: LifecycleStatus,
): boolean => to === CHURNED || from === CHURNED;
